/**
 * /reserva-starlink-mini — Landing de campaña colectiva Starlink Mini.
 *
 * Fase 1 (esta iteración):
 *   • Hero + sticky CTA + trust band + bloque de reserva con contador real
 *   • Metaetiquetas dinámicas (react-helmet-async)
 *   • Fuerza idioma ES y oculta toggle EN/ES
 *
 * Fase 2: video-conditions + guarantee + timeline + comparison + FAQ + CTA final
 * Fase 3: Stripe checkout + emails automáticos
 */
import { useEffect, useMemo, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import {
  CheckCircle2,
  ShieldCheck,
  Truck,
  BadgeEuro,
  ArrowRight,
  Play,
  Languages,
  ChevronDown,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3eb1f7e1-02cf-4acb-8ee2-746b00b03901/artifacts/kuo8ppes_Logo%20Global%20tech%20500x500%20FT.png";

// Bilingual dictionary — the reservation page is user-toggleable ES ⇄ EN.
const I18N = {
  es: {
    tag: "CAMPAÑA ABIERTA · ESPAÑA",
    heroTitle1: "El Starlink Mini",
    heroTitle2: "que viste en el vídeo",
    heroSubtitle: "Starlink ya no lo vende en propiedad. Nosotros sí.",
    chip1: "399 € · ENVÍO GRATIS",
    chip2: "ENTREGA EN 4-6 SEMANAS",
    chip3: "SI NO SALE, TE DEVOLVEMOS TODO",
    reserveBtn: "Reservar mi plaza",
    videoPlaceholder: "Vídeo del producto (9:16). Súbelo desde /admin/reservas.",
    productPhotoLabel: "Foto del producto — súbela desde el panel /admin/reservas",
    photoCaption1: "Starlink Mini + Standard X · Foto real, tomada en nuestra oficina de Rotterdam",
    photoCaption2: "Marc con las cajas — no es render, no es stock",
    trust1t: "Empresa registrada UE", trust1s: "Factura con IVA",
    trust2t: "Garantía legal", trust2s: "3 años",
    trust3t: "Entrega", trust3s: "4 a 6 semanas",
    trust4t: "Devolución 100 %", trust4s: "Si no llegamos al objetivo",
    stickyReserve: "RESERVAR — ",
    productLabel: "Producto",
    productName: "Starlink Mini Kit",
    inStore: "En tienda: 459 €",
    vatInc: "IVA incluido · Envío gratis a toda España",
    deliveryBand: "ENTREGA EN 4 A 6 SEMANAS",
    payToday: "Reservas hoy",
    payTodaySub: "Pago seguro con tarjeta",
    payLater: "Pagas antes del envío",
    payLaterSub: "Cuando la mercancía llega",
    objectiveLabel: "Reservas conseguidas para activar el pedido",
    remaining: "Quedan",
    days: "días",
    missing: (n) => `Faltan ${n} reservas para activar el pedido.`,
    guaranteeInit: (d) => `Cuando lleguemos a 100 reservas cursamos el pedido. Si no lo conseguimos antes del ${d}, te devolvemos el 100 % de tu dinero.`,
    goalReached: "✓ Objetivo alcanzado · pedido activado",
    orderConfirmed: "· Pedido confirmado",
    reservations: "reservas",
    goalMsg: (d) => `El pedido ya está confirmado. Seguimos admitiendo reservas hasta el ${d} — las unidades adicionales entran en el mismo envío.`,
    ctaButton: (a) => `RESERVAR MI PLAZA — ${a}`,
    ctaSub: "Pago seguro con tarjeta · Reembolsable hasta el envío",
    reservePlazaHeader: "Reservar plaza",
    productDetail: "El producto en detalle",
    // NEW Fase 2
    conditionsTag: "CONDICIONES",
    conditionsTitle: "Así funciona la compra colectiva",
    conditionsLead: "Léelo antes de reservar. Sin letra pequeña, sin sorpresas.",
    cond1t: "Reservas con 119,70 €", cond1d: "El 30 % del precio total. Reserva tu plaza en el lote nº 1.",
    cond2t: "Pedido bajo pedido", cond2d: "No hay stock inmediato. Reunimos 100 reservas y cursamos el pedido al fabricante.",
    cond3t: "Recibes en 4-6 semanas", cond3d: "Desde el cierre del lote. Máximo comprometido: 8 semanas.",
    cond4t: "Pagas el saldo antes del envío", cond4d: "279,30 € cuando la mercancía llega a nuestro almacén.",
    cond5t: "Devolución 100 % si no salimos", cond5d: "Si no reunimos 100 reservas antes del cierre, te devolvemos todo automáticamente.",
    cond6t: "Cancelable siempre", cond6d: "Puedes cancelar y recuperar el 100 % en cualquier momento antes del envío.",
    guaranteeTag: "GARANTÍA",
    guaranteeTitle: "Tu dinero está protegido",
    guaranteeText1: "Si no llegamos a 100 reservas, te devolvemos el 100 % automáticamente. No tienes que reclamar ni escribir. Si el objetivo no se alcanza antes del cierre, devolvemos todas las reservas íntegras en 5 días hábiles.",
    guaranteeText2: "Y si cambias de opinión, también. Puedes cancelar tu reserva y recuperar el 100 % en cualquier momento antes del envío.",
    howTag: "CÓMO FUNCIONA",
    howTitle: "Cuatro pasos, sin fricción",
    step1t: "Reservas tu plaza", step1d: "Pagas 119,70 € y entras en el grupo",
    step2t: "Llegamos a 100", step2d: "Cursamos el pedido y te avisamos por email",
    step3t: "4-6 semanas después", step3d: "La mercancía llega y abonas los 279,30 € restantes",
    step4t: "Lo recibes", step4d: "Enviamos en 24-48 h desde el pago del saldo",
    legalBox: "Plazo máximo comprometido: 8 semanas desde el cierre del lote. Si nos retrasamos más allá de esa fecha, te devolvemos la reserva íntegra sin que tengas que pedirlo.",
    compTag: "COMPARATIVA",
    compTitle: "Dónde y a qué precio",
    compCol1: "Dónde", compCol2: "Precio",
    compRow1w: "Starlink oficial", compRow1p: "No vende el equipo — solo alquiler a 10 €/mes",
    compRow2w: "Tiendas online España", compRow2p: "459 € – 501 €",
    compRow3w: "Tu reserva", compRow3p: "399 €",
    faqTag: "PREGUNTAS FRECUENTES",
    faqTitle: "Todo lo que necesitas saber",
    finalTag: "ÚLTIMO PASO",
    finalTitle: "Reserva tu Starlink Mini",
    finalSubtitle: "Solo 119,70 € hoy. El resto cuando llegue. Cero riesgo.",
    countdownLabel: "La campaña cierra en",
    countdownExpired: "Campaña cerrada",
    unitDays: "días", unitHours: "horas", unitMinutes: "min", unitSeconds: "seg",
  },
  en: {
    tag: "OPEN CAMPAIGN · SPAIN",
    heroTitle1: "The Starlink Mini",
    heroTitle2: "you saw in the video",
    heroSubtitle: "Starlink no longer sells it outright. We do.",
    chip1: "€399 · FREE SHIPPING",
    chip2: "DELIVERY IN 4-6 WEEKS",
    chip3: "IF IT DOESN'T HAPPEN, WE REFUND EVERYTHING",
    reserveBtn: "Book my spot",
    videoPlaceholder: "Product video (9:16). Upload it from /admin/reservas.",
    productPhotoLabel: "Product photo — upload it from the /admin/reservas panel",
    photoCaption1: "Starlink Mini + Standard X · Real photo, taken at our Rotterdam office",
    photoCaption2: "Marc with the boxes — not a render, not stock",
    trust1t: "EU-registered company", trust1s: "VAT invoice",
    trust2t: "Legal warranty", trust2s: "3 years",
    trust3t: "Delivery", trust3s: "4 to 6 weeks",
    trust4t: "100 % refund", trust4s: "If we miss the goal",
    stickyReserve: "BOOK — ",
    productLabel: "Product",
    productName: "Starlink Mini Kit",
    inStore: "In store: €459",
    vatInc: "VAT included · Free shipping to all Spain",
    deliveryBand: "DELIVERY IN 4 TO 6 WEEKS",
    payToday: "You pay today",
    payTodaySub: "Secure card payment",
    payLater: "You pay before shipping",
    payLaterSub: "When the goods arrive",
    objectiveLabel: "Bookings collected to activate the order",
    remaining: "Left",
    days: "days",
    missing: (n) => `${n} bookings left to activate the order.`,
    guaranteeInit: (d) => `Once we reach 100 bookings we place the order. If we don't reach it before ${d}, we refund 100% of your money.`,
    goalReached: "✓ Goal reached · order activated",
    orderConfirmed: "· Order confirmed",
    reservations: "bookings",
    goalMsg: (d) => `The order is already confirmed. We keep accepting bookings until ${d} — extra units ship in the same batch.`,
    ctaButton: (a) => `BOOK MY SPOT — ${a}`,
    ctaSub: "Secure card payment · Refundable until shipping",
    reservePlazaHeader: "Book spot",
    productDetail: "The product in detail",
    // NEW Fase 2
    conditionsTag: "CONDITIONS",
    conditionsTitle: "How the group-buy works",
    conditionsLead: "Read before booking. No fine print, no surprises.",
    cond1t: "You book with €119.70", cond1d: "30% of the total price. Reserve your spot in batch #1.",
    cond2t: "Made-to-order", cond2d: "No immediate stock. We gather 100 bookings and place the order with the manufacturer.",
    cond3t: "Delivery in 4-6 weeks", cond3d: "From the batch close date. Maximum commitment: 8 weeks.",
    cond4t: "You pay the balance before shipping", cond4d: "€279.30 when the goods arrive at our warehouse.",
    cond5t: "100% refund if we miss the goal", cond5d: "If we don't collect 100 bookings before the close date, we refund everything automatically.",
    cond6t: "Cancel anytime", cond6d: "You can cancel and recover 100% at any point before shipping.",
    guaranteeTag: "GUARANTEE",
    guaranteeTitle: "Your money is protected",
    guaranteeText1: "If we don't reach 100 bookings, we refund 100% automatically. No claim, no email needed. If the goal isn't reached before the close date, we refund every booking in full within 5 business days.",
    guaranteeText2: "And if you change your mind, same thing. You can cancel your booking and get 100% back at any time before shipping.",
    howTag: "HOW IT WORKS",
    howTitle: "Four steps, no friction",
    step1t: "You book your spot", step1d: "You pay €119.70 and join the batch",
    step2t: "We hit 100", step2d: "We place the order and notify you by email",
    step3t: "4-6 weeks later", step3d: "The goods arrive and you pay the remaining €279.30",
    step4t: "You receive it", step4d: "We ship 24-48h after balance payment",
    legalBox: "Maximum committed delivery: 8 weeks from batch close. If we're delayed beyond that date, we refund your booking in full without needing to ask.",
    compTag: "COMPARISON",
    compTitle: "Where and at what price",
    compCol1: "Where", compCol2: "Price",
    compRow1w: "Starlink official", compRow1p: "Doesn't sell the kit — only rental at €10/month",
    compRow2w: "Spain online stores", compRow2p: "€459 – €501",
    compRow3w: "Your booking", compRow3p: "€399",
    faqTag: "FREQUENTLY ASKED",
    faqTitle: "Everything you need to know",
    finalTag: "LAST STEP",
    finalTitle: "Book your Starlink Mini",
    finalSubtitle: "Only €119.70 today. The rest when it arrives. Zero risk.",
    countdownLabel: "Campaign closes in",
    countdownExpired: "Campaign closed",
    unitDays: "days", unitHours: "hours", unitMinutes: "min", unitSeconds: "sec",
  },
};

const FAQ_ITEMS = {
  es: [
    ["¿Por qué no puedo comprarlo directamente en Starlink?", "Starlink ha dejado de vender el Mini en propiedad al consumidor final. Solo lo ofrece en alquiler a 10 €/mes. Nosotros compramos por lotes al fabricante y te lo entregamos en propiedad, con factura con IVA."],
    ["¿Qué pasa si no llegáis a las 100 reservas?", "Devolvemos el 100 % de tu reserva automáticamente en 5 días hábiles, sin que tengas que reclamar. Es una garantía y está en los términos."],
    ["¿Cuándo lo recibo exactamente?", "Entre 4 y 6 semanas desde el cierre del lote. El plazo máximo comprometido es de 8 semanas. Si nos retrasamos más allá de esa fecha, te devolvemos la reserva íntegra sin que tengas que pedirlo."],
    ["¿Puedo reservar aunque ya hayáis llegado a 100?", "Sí. Cuando llegamos a 100 activamos el pedido, pero seguimos admitiendo reservas hasta la fecha de cierre. Las unidades adicionales entran en el mismo envío."],
    ["¿Puedo cancelar y recuperar mi dinero?", "Sí, en cualquier momento antes del envío. Cancelas desde tu email y recuperas el 100 %."],
    ["¿Qué garantía tiene el producto?", "3 años de garantía legal de la UE, más la garantía del fabricante Starlink. Todo el kit lleva factura con IVA a tu nombre o a nombre de tu empresa."],
    ["¿Cuándo y cómo se paga el resto?", "279,30 € cuando la mercancía llegue a nuestro almacén. Te avisaremos por email y podrás pagar con tarjeta de forma segura antes del envío."],
    ["¿Funciona en toda España?", "Sí, envío gratis a península + Baleares + Canarias + Ceuta y Melilla. El servicio Starlink funciona en toda la geografía española."],
    ["¿Necesito contratar el servicio aparte?", "Sí: el kit es solo el equipo. La suscripción mensual del servicio Starlink la contratas directamente con Starlink en su web (puedes activarla y desactivarla cuando quieras)."],
    ["¿Emitís factura?", "Sí, factura completa con IVA a nombre de persona física o empresa. Añade tu NIF/CIF en el checkout si quieres factura a empresa."],
  ],
  en: [
    ["Why can't I buy it directly from Starlink?", "Starlink has stopped selling the Mini outright to end customers. They only offer it as a €10/month rental. We buy in batches from the manufacturer and deliver it to you as your property, with a VAT invoice."],
    ["What if you don't reach 100 bookings?", "We refund 100% of your booking automatically within 5 business days, no claim needed. It's a guarantee and it's in the terms."],
    ["When exactly will I receive it?", "Between 4 and 6 weeks from batch close. Maximum committed delivery is 8 weeks. If we're delayed beyond that date, we refund your full booking without you needing to ask."],
    ["Can I book even if you've reached 100 already?", "Yes. Once we hit 100 the order is placed, but we keep accepting bookings until the close date. Extra units ship in the same batch."],
    ["Can I cancel and get my money back?", "Yes, at any moment before shipping. Cancel via email and get 100% back."],
    ["What warranty does the product have?", "3 years EU legal warranty plus the Starlink manufacturer's warranty. The kit ships with a VAT invoice in your name (personal or business)."],
    ["When and how do I pay the rest?", "€279.30 when the goods arrive at our warehouse. We'll email you and you can pay securely by card before shipping."],
    ["Does it work in all of Spain?", "Yes — free shipping to Peninsula + Balearic + Canary Islands + Ceuta and Melilla. Starlink service works across all Spanish territory."],
    ["Do I need to subscribe to the service separately?", "Yes: the kit is only the hardware. The monthly Starlink service is contracted directly with Starlink on their site (activate and deactivate anytime)."],
    ["Do you issue invoices?", "Yes, full VAT invoice — personal or company name. Add your VAT number in checkout for a company invoice."],
  ],
};


const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.21, 0.47, 0.32, 0.98] },
};

const fmtEUR = (n, locale) =>
  new Intl.NumberFormat(locale === "en" ? "en-IE" : "es-ES", { style: "currency", currency: "EUR" }).format(n);

function useCountdown(fechaCierre) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return useMemo(() => {
    if (!fechaCierre) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true, totalMs: 0 };
    const end = new Date(fechaCierre).getTime();
    const diff = end - now;
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true, totalMs: 0 };
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { days, hours, minutes, seconds, expired: false, totalMs: diff };
  }, [fechaCierre, now]);
}

export default function ReservaStarlinkMiniPage() {
  const { language, setLanguage } = useLanguage();
  const [config, setConfig] = useState(null);
  const bookingRef = useRef(null);
  const t = I18N[language] || I18N.es;
  const locale = language === "en" ? "en-IE" : "es-ES";

  // Default this page to Spanish on every mount. The toggle in the header
  // still lets the user switch to EN — but on a fresh visit / reload we
  // always start in ES (the campaign targets Spanish consumers).
  useEffect(() => {
    setLanguage("es");
  }, [setLanguage]);

  // Fetch live config every 60s
  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/starlink/config`);
        if (!stop) setConfig(r.data);
      } catch (e) {
        /* silent */
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { stop = true; clearInterval(id); };
  }, []);

  const scrollToBooking = () => {
    bookingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const objetivo = config?.objetivo_reservas ?? 100;
  const actuales = config?.reservas_actuales ?? 0;
  const alcanzado = actuales >= objetivo;
  const reembolsoAuto = config?.campana_resuelta_reembolso === true;
  const restantes = Math.max(0, objetivo - actuales);
  const pct = Math.min(100, (actuales / objetivo) * 100);
  const countdown = useCountdown(config?.fecha_cierre);
  const fechaCierreLabel = config?.fecha_cierre
    ? new Date(config.fecha_cierre).toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" })
    : "—";

  const pageTitle = language === "en"
    ? "Starlink Mini €399 — Book your spot | GTX"
    : "Starlink Mini 399 € — Reserva tu plaza | GTX";
  const pageDescription = language === "en"
    ? "Starlink Mini €399 with free shipping to Spain. Delivery in 4-6 weeks. Book with €119.70 and 100% refund guaranteed."
    : "Starlink Mini a 399 € con envío gratis a España. Entrega en 4-6 semanas. Reserva con 119,70 € y devolución del 100 % garantizada.";

  useEffect(() => {
    // Imperative title + <html lang> update — bypasses a react-helmet-async
    // bug with React 19 that trips on interpolated <title> children.
    document.title = pageTitle;
    document.documentElement.lang = language;
  }, [pageTitle, language]);

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white noise" data-testid="starlink-reserva-page">
      <Helmet>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="product" />
        <meta property="og:locale" content={language === "en" ? "en_GB" : "es_ES"} />
        {config?.og_image_url && <meta property="og:image" content={config.og_image_url} />}
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <StarlinkHeader onCTA={scrollToBooking} language={language} setLanguage={setLanguage} t={t} />

      <div className="h-24 lg:h-28" />

      {/* HERO */}
      <section className="relative pt-6 lg:pt-10 pb-16 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 mesh-hero pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <motion.div {...fadeUp}>
            <span className="inline-block text-[10px] font-bold tracking-[0.22em] uppercase text-blue-400 mono mb-4">
              {t.tag}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mb-5">
              {t.heroTitle1} <br /> {t.heroTitle2}
            </h1>
            <p className="text-base sm:text-lg text-white/70 mb-7 max-w-lg leading-relaxed">{t.heroSubtitle}</p>
            <div className="flex flex-wrap gap-2 mb-6">
              <Chip>{t.chip1}</Chip>
              <Chip>{t.chip2}</Chip>
              <Chip>{t.chip3}</Chip>
            </div>

            {!reembolsoAuto && !countdown.expired && (
              <CountdownHero countdown={countdown} t={t} />
            )}

            <button
              type="button"
              onClick={scrollToBooking}
              data-testid="hero-reserve-btn"
              className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white rounded-lg px-8 py-4 text-base font-semibold transition-all hover:shadow-[0_8px_40px_-8px_rgba(59,130,246,0.7)] hover:-translate-y-0.5"
            >
              {t.reserveBtn}
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>

          <motion.div {...fadeUp} className="relative">
            <VideoPlayer
              url={config?.video_hero_url}
              aspectClass="aspect-[9/16] max-w-sm mx-auto"
              placeholder={t.videoPlaceholder}
            />
          </motion.div>
        </div>
      </section>

      <StickyCTA
        precio={config?.precio_total ?? 399}
        importeReserva={config?.importe_reserva ?? 119.7}
        plazo={t.trust3s}
        onClick={scrollToBooking}
        t={t}
        locale={locale}
      />

      {/* TRUST BAND */}
      <section className="border-y border-white/8 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          <Trust icon={<BadgeEuro className="w-5 h-5" />} title={t.trust1t} subtitle={t.trust1s} />
          <Trust icon={<ShieldCheck className="w-5 h-5" />} title={t.trust2t} subtitle={t.trust2s} />
          <Trust icon={<Truck className="w-5 h-5" />} title={t.trust3t} subtitle={config?.plazo_entrega || t.trust3s} />
          <Trust icon={<CheckCircle2 className="w-5 h-5" />} title={t.trust4t} subtitle={t.trust4s} />
        </div>
      </section>

      {/* PHOTO SECTION — real product + human proof, side by side */}
      <section className="py-16 lg:py-20 border-b border-white/8">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="inline-block text-[10px] font-bold tracking-[0.22em] uppercase text-blue-400 mono mb-4">
            {t.tag}
          </div>
          <h2 className="text-3xl lg:text-5xl font-black tracking-tight mb-8">{t.productDetail}</h2>
          <div className="grid md:grid-cols-2 gap-5 lg:gap-6">
            <ProductPhoto url={config?.imagen_producto_url} caption={t.photoCaption1} placeholder={t.productPhotoLabel} />
            <ProductPhoto url={config?.imagen_prueba_url} caption={t.photoCaption2} placeholder={t.productPhotoLabel} />
          </div>
        </div>
      </section>

      {/* RESERVATION BLOCK */}
      <section ref={bookingRef} id="reservar" className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="rounded-2xl border border-blue-500/30 bg-[#0F0F16] overflow-hidden shadow-[0_20px_60px_-20px_rgba(59,130,246,0.3)]">
            <div className="px-6 lg:px-10 pt-8 lg:pt-10 pb-6">
              <div className="flex items-start justify-between flex-wrap gap-4 mb-2">
                <div>
                  <div className="inline-block text-[10px] font-bold tracking-[0.22em] uppercase text-blue-400 mono mb-2">
                    {t.productLabel}
                  </div>
                  <div className="text-2xl lg:text-3xl font-black">{t.productName}</div>
                </div>
                <div className="text-right">
                  <div className="text-white/40 text-sm line-through mono">{t.inStore}</div>
                  <div className="text-5xl lg:text-6xl font-black leading-none tracking-tight">
                    {fmtEUR(config?.precio_total ?? 399, locale)}
                  </div>
                  <div className="text-white/50 text-xs mt-1">{t.vatInc}</div>
                </div>
              </div>
            </div>

            <div className="bg-blue-500 text-white text-center py-3 font-bold text-sm tracking-[0.15em] uppercase">
              {t.deliveryBand}
            </div>

            <div className="px-6 lg:px-10 py-8 grid sm:grid-cols-2 gap-4 border-b border-white/8">
              <PayCol
                label={t.payToday}
                amount={config?.importe_reserva ?? 119.7}
                sublabel={t.payTodaySub}
                highlight
                locale={locale}
              />
              <PayCol
                label={t.payLater}
                amount={config?.importe_saldo ?? 279.3}
                sublabel={t.payLaterSub}
                locale={locale}
              />
            </div>

            <div className="px-6 lg:px-10 py-8">
              {reembolsoAuto ? (
                <>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/15 text-orange-300 border border-orange-500/40 text-xs font-bold uppercase tracking-widest mb-3" data-testid="banner-refunded">
                    Campaña cerrada · reembolso automático en curso
                  </div>
                  <div className="text-2xl lg:text-3xl font-black tracking-tight mb-2">
                    No hemos llegado al cupo de {objetivo}
                  </div>
                  <p className="text-sm text-white/70 leading-relaxed">
                    Cerramos la campaña con {actuales} {t.reservations}. Todas las reservas se reembolsan automáticamente al 100 %. Si tenías una reserva, recibirás el dinero en tu tarjeta en 5-10 días hábiles según tu banco — sin necesidad de reclamar.
                  </p>
                </>
              ) : alcanzado ? (
                <>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 text-xs font-bold uppercase tracking-widest mb-3">
                    {t.goalReached}
                  </div>
                  <div className="text-3xl lg:text-4xl font-black tracking-tight mb-1">
                    {actuales} {t.reservations}
                    <span className="text-emerald-400"> {t.orderConfirmed}</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden mb-3">
                    <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500" style={{ width: "100%" }} />
                  </div>
                  <p className="text-sm text-white/60">{t.goalMsg(fechaCierreLabel)}</p>
                </>
              ) : (
                <>
                  <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-white/50 mb-2">
                    {t.objectiveLabel}
                  </div>
                  <div className="flex items-baseline justify-between mb-3">
                    <div className="text-5xl lg:text-6xl font-black tracking-tight" style={{ color: "#F5A524" }}>
                      {actuales}
                      <span className="text-white/30 text-2xl lg:text-3xl"> / {objetivo}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] uppercase tracking-widest text-white/40">{t.remaining}</div>
                      <div className="text-2xl font-bold">{countdown.days} {t.days}</div>
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden mb-3">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.9, ease: "easeOut" }}
                      className="h-full"
                      style={{ background: "linear-gradient(90deg, #F5A524 0%, #F5C34A 100%)" }}
                    />
                  </div>
                  <p className="text-sm text-white/60 leading-relaxed">
                    {t.missing(restantes)} {t.guaranteeInit(fechaCierreLabel)}
                  </p>
                </>
              )}
            </div>

            <div className="px-6 lg:px-10 pb-8 lg:pb-10">
              <Link to={reembolsoAuto ? "#" : "/reserva-starlink-mini/checkout"} onClick={(e) => reembolsoAuto && e.preventDefault()}>
                <button
                  type="button"
                  disabled={reembolsoAuto}
                  data-testid="booking-cta-btn"
                  className="w-full bg-blue-500 hover:bg-blue-400 disabled:bg-white/10 disabled:text-white/40 disabled:cursor-not-allowed text-white rounded-xl px-6 py-5 text-lg lg:text-xl font-black tracking-wide transition-all hover:shadow-[0_12px_50px_-10px_rgba(59,130,246,0.7)] hover:-translate-y-0.5"
                >
                  {reembolsoAuto ? "Campaña cerrada" : t.ctaButton(fmtEUR(config?.importe_reserva ?? 119.7, locale))}
                </button>
              </Link>
              <p className="text-center text-xs text-white/40 mt-3">{t.ctaSub}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CONDITIONS (text, no video) ============ */}
      <section className="py-16 lg:py-24 border-t border-white/8" id="condiciones">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="inline-block text-[10px] font-bold tracking-[0.22em] uppercase text-blue-400 mono mb-3">{t.conditionsTag}</div>
          <h2 className="text-3xl lg:text-5xl font-black tracking-tight mb-3">{t.conditionsTitle}</h2>
          <p className="text-white/60 text-base lg:text-lg mb-10 max-w-2xl">{t.conditionsLead}</p>
          <div className="grid md:grid-cols-2 gap-4">
            <ConditionCard n="01" title={t.cond1t} desc={t.cond1d} />
            <ConditionCard n="02" title={t.cond2t} desc={t.cond2d} />
            <ConditionCard n="03" title={t.cond3t} desc={t.cond3d} />
            <ConditionCard n="04" title={t.cond4t} desc={t.cond4d} />
            <ConditionCard n="05" title={t.cond5t} desc={t.cond5d} highlight />
            <ConditionCard n="06" title={t.cond6t} desc={t.cond6d} highlight />
          </div>
        </div>
      </section>

      {/* ============ REFUND GUARANTEE ============ */}
      <section className="py-16 lg:py-20 border-t border-white/8 bg-gradient-to-b from-blue-950/20 via-transparent to-transparent">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="rounded-2xl border border-blue-500/40 bg-[#0F0F16] p-8 lg:p-12">
            <div className="flex items-start gap-4 lg:gap-6">
              <div className="shrink-0 w-14 h-14 rounded-xl bg-blue-500/15 border border-blue-500/40 flex items-center justify-center">
                <ShieldCheck className="w-7 h-7 text-blue-300" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-blue-400 mono mb-2">{t.guaranteeTag}</div>
                <h2 className="text-2xl lg:text-4xl font-black tracking-tight mb-4">{t.guaranteeTitle}</h2>
                <p className="text-white/75 leading-relaxed mb-3">{t.guaranteeText1}</p>
                <p className="text-white/75 leading-relaxed">{t.guaranteeText2}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS — 4 steps timeline ============ */}
      <section className="py-16 lg:py-24 border-t border-white/8">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="inline-block text-[10px] font-bold tracking-[0.22em] uppercase text-blue-400 mono mb-3">{t.howTag}</div>
          <h2 className="text-3xl lg:text-5xl font-black tracking-tight mb-10">{t.howTitle}</h2>
          <div className="grid md:grid-cols-4 gap-4 lg:gap-6">
            <StepCard n="01" title={t.step1t} desc={t.step1d} />
            <StepCard n="02" title={t.step2t} desc={t.step2d} />
            <StepCard n="03" title={t.step3t} desc={t.step3d} />
            <StepCard n="04" title={t.step4t} desc={t.step4d} />
          </div>
          <div className="mt-8 p-4 rounded-lg border border-white/15 bg-white/[0.02] text-xs text-white/60 leading-relaxed">
            {t.legalBox}
          </div>
        </div>
      </section>

      {/* ============ COMPARISON ============ */}
      <section className="py-16 lg:py-20 border-t border-white/8">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="inline-block text-[10px] font-bold tracking-[0.22em] uppercase text-blue-400 mono mb-3">{t.compTag}</div>
          <h2 className="text-3xl lg:text-5xl font-black tracking-tight mb-8">{t.compTitle}</h2>
          <div className="rounded-2xl border border-white/10 bg-[#0F0F16] overflow-hidden">
            <div className="grid grid-cols-[1fr_1.4fr] px-5 lg:px-7 py-3 border-b border-white/10 bg-white/[0.03] text-[11px] font-bold tracking-widest uppercase text-white/50">
              <span>{t.compCol1}</span><span>{t.compCol2}</span>
            </div>
            <CompRow w={t.compRow1w} p={t.compRow1p} />
            <CompRow w={t.compRow2w} p={t.compRow2p} />
            <CompRow w={t.compRow3w} p={t.compRow3p} highlight />
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="py-16 lg:py-24 border-t border-white/8">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="inline-block text-[10px] font-bold tracking-[0.22em] uppercase text-blue-400 mono mb-3">{t.faqTag}</div>
          <h2 className="text-3xl lg:text-5xl font-black tracking-tight mb-8">{t.faqTitle}</h2>
          <div className="space-y-2" data-testid="starlink-faq">
            {(FAQ_ITEMS[language] || FAQ_ITEMS.es).map(([q, a], i) => (
              <FaqItem key={i} q={q} a={a} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-20 lg:py-28 border-t border-blue-500/25 bg-gradient-to-b from-blue-950/25 via-transparent to-transparent">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <div className="inline-block text-[10px] font-bold tracking-[0.22em] uppercase text-blue-400 mono mb-3">{t.finalTag}</div>
          <h2 className="text-4xl lg:text-6xl font-black tracking-tight mb-4">{t.finalTitle}</h2>
          <p className="text-white/70 text-lg mb-8">{t.finalSubtitle}</p>
          <div className="flex items-baseline justify-center gap-4 mb-6">
            <span className="text-3xl font-black" style={{ color: "#F5A524" }}>{actuales}</span>
            <span className="text-white/40 text-lg">/ {objetivo} {t.reservations.toLowerCase()}</span>
          </div>
          <Link to="/reserva-starlink-mini/checkout">
            <button
              type="button"
              data-testid="final-cta-btn"
              className="bg-blue-500 hover:bg-blue-400 text-white rounded-xl px-8 lg:px-12 py-5 text-lg lg:text-xl font-black tracking-wide transition-all hover:shadow-[0_12px_50px_-10px_rgba(59,130,246,0.7)] hover:-translate-y-0.5"
            >
              {t.ctaButton(fmtEUR(config?.importe_reserva ?? 119.7, locale))}
            </button>
          </Link>
          <p className="text-xs text-white/40 mt-4">{t.ctaSub}</p>
        </div>
      </section>

      <footer className="border-t border-white/8 py-10 mt-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-wrap justify-between gap-4 text-xs text-white/40">
          <div>
            Global Tech Xperts B.V. · Westblaak 92, Unit 3.04, 3012 KM Rotterdam
            <br />
            KvK 97248991 · VAT NL867969015B01
          </div>
          <div className="flex gap-4">
            <a href="mailto:adm@globaltechxperts.nl" className="hover:text-white">adm@globaltechxperts.nl</a>
            <a href="https://wa.me/31645282452" target="_blank" rel="noopener noreferrer" className="hover:text-white">WhatsApp +31 645 282 452</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function StarlinkHeader({ onCTA, language, setLanguage, t }) {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0F]/85 backdrop-blur-lg border-b border-white/8">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 lg:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="GTX" className="w-9 h-9 rounded-md" />
          <div className="hidden sm:block">
            <div className="text-sm font-black tracking-tight leading-none">Global Tech Xperts</div>
            <div className="text-[9px] tracking-[0.2em] uppercase text-white/40 mt-1">B.V. · Rotterdam</div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          {/* Language toggle */}
          <div className="hidden sm:flex items-center gap-1 rounded-md bg-white/5 border border-white/10 p-0.5 text-[11px] font-bold" data-testid="lang-toggle">
            <button
              type="button"
              onClick={() => setLanguage("es")}
              data-testid="lang-es"
              className={`px-2.5 py-1 rounded ${language === "es" ? "bg-blue-500 text-white" : "text-white/60 hover:text-white"}`}
            >
              ES
            </button>
            <button
              type="button"
              onClick={() => setLanguage("en")}
              data-testid="lang-en"
              className={`px-2.5 py-1 rounded ${language === "en" ? "bg-blue-500 text-white" : "text-white/60 hover:text-white"}`}
            >
              EN
            </button>
          </div>
          <button
            type="button"
            onClick={() => setLanguage(language === "es" ? "en" : "es")}
            aria-label="Toggle language"
            className="sm:hidden w-8 h-8 flex items-center justify-center rounded-md bg-white/5 border border-white/10 text-white/60 hover:text-white"
          >
            <Languages className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onCTA}
            data-testid="header-reserve-btn"
            className="bg-blue-500 hover:bg-blue-400 text-white rounded-md px-4 lg:px-5 h-9 lg:h-10 text-sm font-semibold transition-all hover:shadow-[0_0_30px_-6px_rgba(59,130,246,0.7)]"
          >
            {t.reservePlazaHeader}
          </button>
        </div>
      </div>
    </header>
  );
}

function StickyCTA({ precio, importeReserva, plazo, onClick, t, locale }) {
  return (
    <div
      className="fixed z-40 bottom-4 left-4 right-4 sm:left-1/2 sm:-translate-x-1/2 sm:bottom-6 sm:max-w-3xl lg:top-24 lg:bottom-auto lg:right-6 lg:left-auto lg:translate-x-0 lg:max-w-xl"
      data-testid="sticky-cta"
    >
      <div className="rounded-xl bg-[#12121A]/95 backdrop-blur-xl border border-white/10 shadow-2xl px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-white/50">{fmtEUR(precio, locale)} · {plazo}</div>
          <div className="text-sm font-semibold truncate">Starlink Mini</div>
        </div>
        <button
          type="button"
          onClick={onClick}
          data-testid="sticky-cta-btn"
          className="shrink-0 bg-blue-500 hover:bg-blue-400 text-white rounded-md px-4 h-10 text-sm font-bold transition-all hover:shadow-[0_0_25px_-6px_rgba(59,130,246,0.7)]"
        >
          {t.stickyReserve}{fmtEUR(importeReserva, locale)}
        </button>
      </div>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-block text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.03] text-white/80">
      {children}
    </span>
  );
}

function CountdownHero({ countdown, t }) {
  const urgent = countdown.totalMs > 0 && countdown.totalMs < 86400000; // <24h
  const veryUrgent = countdown.totalMs > 0 && countdown.totalMs < 3600000; // <1h
  const color = veryUrgent ? "#EF4444" : urgent ? "#F5A524" : "#F5A524";
  const label = veryUrgent
    ? "¡Últimos minutos!"
    : urgent
      ? "Últimas horas"
      : t.countdownLabel;

  return (
    <div
      className="mb-8 inline-block rounded-2xl border p-5 lg:p-6 min-w-[300px]"
      style={{ borderColor: `${color}55`, background: `${color}0d` }}
      data-testid="countdown-hero"
    >
      <div className="text-[10px] font-bold tracking-[0.22em] uppercase mono mb-3" style={{ color }}>
        {label}
      </div>
      <div className="flex items-baseline gap-2.5" data-testid="countdown-values">
        <CountdownCell v={countdown.days} label={t.unitDays} color={color} />
        <CountdownSep color={color} />
        <CountdownCell v={countdown.hours} label={t.unitHours} color={color} pad />
        <CountdownSep color={color} />
        <CountdownCell v={countdown.minutes} label={t.unitMinutes} color={color} pad />
        <CountdownSep color={color} />
        <CountdownCell v={countdown.seconds} label={t.unitSeconds} color={color} pad small />
      </div>
    </div>
  );
}

function CountdownCell({ v, label, color, pad = false, small = false }) {
  const value = pad ? String(v).padStart(2, "0") : String(v);
  return (
    <div className="text-center">
      <div
        className={`font-black tracking-tight leading-none mono ${small ? "text-3xl lg:text-4xl" : "text-4xl lg:text-5xl"}`}
        style={{ color }}
      >
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1.5">{label}</div>
    </div>
  );
}

function CountdownSep({ color }) {
  return <div className="text-3xl lg:text-4xl font-black leading-none mono self-start" style={{ color: `${color}70`, marginTop: 2 }}>:</div>;
}

function Trust({ icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <div className="shrink-0 w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/25 text-blue-400 flex items-center justify-center">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-bold">{title}</div>
        <div className="text-xs text-white/50 mt-0.5">{subtitle}</div>
      </div>
    </div>
  );
}

function PayCol({ label, amount, sublabel, highlight = false, locale }) {
  return (
    <div
      className={`p-5 rounded-xl border ${
        highlight ? "border-blue-500/40 bg-blue-500/[0.06]" : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-white/50 mb-2">{label}</div>
      <div className={`text-3xl lg:text-4xl font-black tracking-tight ${highlight ? "text-blue-300" : ""}`}>
        {fmtEUR(amount, locale)}
      </div>
      <div className="text-xs text-white/40 mt-1.5">{sublabel}</div>
    </div>
  );
}

function VideoPlayer({ url, aspectClass, placeholder }) {
  if (url) {
    return (
      <div className={`${aspectClass} rounded-2xl overflow-hidden border border-white/10 bg-black`}>
        <video src={url} controls playsInline className="w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div
      className={`${aspectClass} rounded-2xl overflow-hidden border border-blue-500/30 bg-gradient-to-br from-blue-950/60 to-black flex flex-col items-center justify-center text-center px-6`}
    >
      <div className="w-16 h-16 rounded-full bg-blue-500/15 border border-blue-400/30 flex items-center justify-center mb-3">
        <Play className="w-6 h-6 text-blue-300" />
      </div>
      <div className="text-sm text-white/60 max-w-[220px]">{placeholder}</div>
    </div>
  );
}

function ProductPhoto({ url, caption, placeholder }) {  if (url) {
    return (
      <figure className="rounded-2xl overflow-hidden border border-blue-500/30 bg-black">
        <img src={url} alt={caption || "Starlink Mini"} className="w-full aspect-[4/5] object-cover" loading="lazy" />
        {caption && (
          <figcaption className="px-4 py-3 text-xs text-white/60 border-t border-white/10 bg-white/[0.02]">
            {caption}
          </figcaption>
        )}
      </figure>
    );
  }
  return (
    <div
      className="aspect-[4/5] rounded-2xl border-2 border-dashed border-blue-500/30 bg-gradient-to-br from-blue-950/40 to-black flex items-center justify-center text-center px-6"
      data-testid="product-photo-placeholder"
    >
      <div className="text-sm text-white/50 max-w-md">{placeholder}</div>
    </div>
  );
}

function ConditionCard({ n, title, desc, highlight = false }) {
  return (
    <div
      className={`p-5 lg:p-6 rounded-xl border ${
        highlight
          ? "border-blue-500/40 bg-blue-500/[0.06]"
          : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className={`text-xs font-bold tracking-[0.18em] uppercase mono mb-2 ${highlight ? "text-blue-400" : "text-white/40"}`}>
        {n}
      </div>
      <div className="text-lg font-bold mb-1.5">{title}</div>
      <div className="text-sm text-white/60 leading-relaxed">{desc}</div>
    </div>
  );
}

function StepCard({ n, title, desc }) {
  return (
    <div className="p-5 lg:p-6 rounded-xl border border-white/10 bg-white/[0.02] relative">
      <div className="absolute top-4 right-4 text-4xl font-black text-blue-500/25 mono leading-none">{n}</div>
      <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-blue-400 mono mb-2">Paso {n}</div>
      <div className="text-lg font-bold mb-2 pr-8">{title}</div>
      <div className="text-sm text-white/60 leading-relaxed">{desc}</div>
    </div>
  );
}

function CompRow({ w, p, highlight = false }) {
  return (
    <div
      className={`grid grid-cols-[1fr_1.4fr] px-5 lg:px-7 py-4 border-b border-white/8 last:border-b-0 ${
        highlight ? "bg-blue-500/[0.08]" : ""
      }`}
    >
      <span className={`text-sm ${highlight ? "text-white font-bold" : "text-white/80"}`}>{w}</span>
      <span className={`text-sm ${highlight ? "text-blue-300 font-black text-lg" : "text-white/60"}`}>{p}</span>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-lg border transition-colors ${open ? "border-blue-500/30 bg-blue-500/[0.04]" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.03]"}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left"
        aria-expanded={open}
      >
        <span className="text-sm lg:text-base font-semibold">{q}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-white/50 transition-transform ${open ? "rotate-180 text-blue-300" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm text-white/70 leading-relaxed border-t border-white/8 pt-3">
          {a}
        </div>
      )}
    </div>
  );
}
